import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { RevealLines, FadeUp } from "@/components/Reveal";

const FORTUNES = [
  ["Clarity", "The fog is lifting. The answer you keep circling is simpler than you think."],
  ["Clarity", "Write it down tonight. What you name, you can finally see."],
  ["Love", "Someone thinks of you more often than they say. Let them."],
  ["Love", "Be as gentle with yourself as you are with the people you love."],
  ["Peace", "You are allowed to rest before everything is finished."],
  ["Peace", "The quiet you're craving is closer than the next big change."],
  ["Protection", "Not every door deserves your key. Keep a little of your light for you."],
  ["Protection", "What drained you yesterday has no power over tomorrow."],
  ["Calm", "Breathe like the tide: slow in, slower out. The waves will wait."],
  ["Calm", "A small kindness today will ripple further than you'll ever know."],
  ["Clarity", "Stop asking everyone else. You already know what you want."],
  ["Clarity", "The path looks tangled only because you're standing too close. Step back."],
  ["Clarity", "Trust the thought that keeps returning at 3 a.m. It's trying to tell you something."],
  ["Clarity", "Not every question needs an answer tonight. Some need a walk under the stars."],
  ["Clarity", "Let go of one thing that no longer fits, and watch everything else make sense."],
  ["Clarity", "The moon only shows half her face, and she still lights the whole sky."],
  ["Love", "The right people will never make you beg for a place at their table."],
  ["Love", "A message you've been waiting for is closer than you think."],
  ["Love", "Love that's meant for you will wait eight hundred years if it has to."],
  ["Love", "Tell someone today why they matter. Words are a kind of magic."],
  ["Love", "Your softness is not a weakness. It's the reason people feel safe with you."],
  ["Love", "Someone is grateful you exist, even if they've never said it out loud."],
  ["Peace", "You don't have to carry yesterday into tomorrow. Set it down here."],
  ["Peace", "The flowers don't rush, and still, everything blooms."],
  ["Peace", "Forgive the version of you who didn't know better. She was trying."],
  ["Peace", "Tonight, let the stars hold your worries. They've held heavier things."],
  ["Peace", "Slow mornings and warm tea are allowed to be the whole plan."],
  ["Peace", "Not everything broken needs fixing. Some things just need time."],
  ["Protection", "You can be kind and still say no. Both are a kind of strength."],
  ["Protection", "Your energy is precious. Spend it on people who give it back."],
  ["Protection", "A closed door is sometimes the universe guarding you."],
  ["Protection", "Wear your light like armor. Nothing dark can stay where you shine."],
  ["Protection", "Someone unseen is looking out for you, more than you know."],
  ["Protection", "Walk away from what dims you. The moon will light your way home."],
  ["Calm", "The storm in your chest will pass. You've outlasted every one before."],
  ["Calm", "Put your hand on your heart. Hear that? You're still here, and that's enough."],
  ["Calm", "Not everything urgent is important. Breathe first, then choose."],
  ["Calm", "Be like water: soft, patient and strong enough to shape stone."],
  ["Calm", "Today, do one small thing slowly, and do it with love."],
  ["Calm", "Lavender grows in tough soil. So do you."],
];

const Moon = ({ spinning, onClick }) => (
  <button onClick={onClick} disabled={spinning} data-testid="oracle-moon-btn" aria-label="Ask the moon"
    className="relative mx-auto block w-56 h-56 md:w-72 md:h-72 rounded-full group disabled:cursor-wait">
    <span className="absolute -inset-4 rounded-full bg-purple-400/30 blur-2xl transition-opacity duration-700 group-hover:opacity-100 opacity-70" />
    <motion.span animate={spinning ? { rotate: 360, scale: [1, 1.08, 1] } : { rotate: 0 }}
      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 rounded-full shadow-[0_0_80px_rgba(233,213,255,0.45)]"
      style={{ background: "radial-gradient(circle at 35% 30%, #fff8e7, #e9d5ff 40%, #a78bfa 75%, #6d28d9)" }}>
      <span className="absolute w-10 h-10 rounded-full bg-purple-400/30 top-[22%] left-[55%]" />
      <span className="absolute w-6 h-6 rounded-full bg-purple-400/30 top-[58%] left-[28%]" />
      <span className="absolute w-14 h-14 rounded-full bg-purple-400/25 top-[60%] left-[58%]" />
    </motion.span>
    <span className="absolute inset-0 grid place-items-center font-display italic text-2xl text-[#2a1640] transition-transform duration-500 group-hover:scale-105">
      {spinning ? "listening…" : "Tap the moon"}
    </span>
  </button>
);

const Reading = ({ reading, product, onAdd }) => (
  <motion.div key={reading[1]} initial={{ opacity: 0, y: 30, rotateX: -25 }} animate={{ opacity: 1, y: 0, rotateX: 0 }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} style={{ transformPerspective: 900 }}
    className="rounded-[24px] border border-purple-200/25 bg-[#170a24]/85 backdrop-blur-xl p-6 md:p-8" data-testid="oracle-reading">
    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-200">Your intention · {reading[0]}</span>
    <p className="mt-4 font-display text-3xl md:text-4xl leading-snug" data-testid="oracle-message">“{reading[1]}”</p>
    {product && (
      <div className="mt-7 flex items-center gap-4 rounded-2xl bg-[#0e0616]/60 p-3" data-testid="oracle-product">
        <img src={product.image} alt={product.name} className="w-16 h-16 rounded-xl object-cover" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-purple-200">The moon picked</p>
          <p className="font-display text-xl truncate">{product.name}</p>
          <p className="font-mono text-sm text-amber-200">{money(product.price)}</p>
        </div>
        <button onClick={onAdd} data-testid="oracle-add-to-cart-btn" aria-label={`Add ${product.name} to cart`}
          className="shrink-0 w-11 h-11 rounded-full bg-purple-200 text-[#0e0616] grid place-items-center hover:bg-amber-200 transition-colors">
          <ShoppingBag className="w-4 h-4" />
        </button>
      </div>
    )}
  </motion.div>
);

export const Oracle = () => {
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [reading, setReading] = useState(null);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    http.get("/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (!spinning) return undefined;
    const t = setTimeout(() => {
      setReading((prev) => {
        const pool = FORTUNES.filter((f) => f !== prev);
        return pool[Math.floor(Math.random() * pool.length)];
      });
      setSpinning(false);
    }, 1400);
    return () => clearTimeout(t);
  }, [spinning]);

  const matches = reading ? products.filter((p) => p.intention === reading[0]) : [];
  const product = matches[0] ?? products[0];
  const addPick = () => { if (product) { add(product); toast(`${product.name} is in your cart`); } };

  return (
    <section id="oracle" className="relative py-24 md:py-36 overflow-hidden" data-testid="oracle-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-purple-200 mb-6 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-200" /> The Moon Oracle</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.92] tracking-tight">
            <RevealLines inView lines={[<>Ask the moon</>, <em className="text-amber-200">a question.</em>]} />
          </h2>
          <FadeUp className="mt-6 max-w-md text-purple-50/90 leading-relaxed">
            Hold a question in your heart, then tap the moon. It will whisper a message and choose a bracelet for you.
          </FadeUp>
        </div>
        <div className="lg:col-span-7 grid md:grid-cols-2 gap-10 items-center">
          <Moon spinning={spinning} onClick={() => setSpinning(true)} />
          <div className="min-h-[260px] flex flex-col justify-center">
            {reading ? (
              <>
                <Reading reading={reading} product={product} onAdd={addPick} />
                <button onClick={() => setSpinning(true)} disabled={spinning} data-testid="oracle-again-btn"
                  className="mt-4 self-start inline-flex items-center gap-2 text-sm text-purple-100 hover:text-amber-200 transition-colors">
                  <RefreshCw className="w-4 h-4" /> Ask again
                </button>
              </>
            ) : (
              <p className="font-display italic text-2xl text-purple-100/90 text-center md:text-left" data-testid="oracle-placeholder">Your message will appear here…</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
